'use client';

import { Image } from '@ahafood/ui/components/image';
import { assetPrefix, type Address } from '@ahm/api-wrappers-core';
import { useEffect, useState } from 'react';
import { useCartStore } from '@/store/cart/cart-store-provider';

interface LocationLoadingScreenProps {
  onComplete?: () => void;
  duration?: number;
  pendingAddress?: Address | null;
}

export default function LocationLoadingScreen({
  onComplete,
  duration = 2500,
  pendingAddress,
}: LocationLoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Đang tải địa chỉ đã lưu');
  const address = useCartStore((state) => state.address);

  // Use pendingAddress if available, otherwise fall back to current address
  const displayAddress = pendingAddress ?? address;

  useEffect(() => {
    const textInterval = setInterval(() => {
      setLoadingText((prev) => {
        if (prev.endsWith('...')) {
          return 'Đang tải địa chỉ đã lưu';
        }
        return prev + '.';
      });
    }, 500);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          clearInterval(textInterval);
          setTimeout(() => {
            onComplete?.();
          }, 300);
          return 100;
        }
        return prev + 4;
      });
    }, duration / 25);

    return () => {
      clearInterval(progressInterval);
      clearInterval(textInterval);
    };
  }, [duration, onComplete]);

  return (
    <div className="mt-4 rounded-lg bg-white p-6 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="size-48 border border-primary-50 rounded-full animate-ping opacity-30" />
          <div
            className="absolute inset-2 border border-primary-40 rounded-full animate-ping opacity-40"
            style={{ animationDelay: '0.5s' }}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col items-center space-y-6 relative z-10">
        {/* Loading text */}
        <div className="text-center">
          <h3 className="text-sm font-medium text-neutral-70">{loadingText}</h3>
        </div>

        {/* AI Robot using Skeleton and simple div structure */}
        <div className="relative size-48 aspect-square overflow-hidden">
          <Image
            src={`${assetPrefix}/static/images/bg-banner-robot.webp`}
            alt="active-image"
            fill
            style={{ objectFit: 'contain' }}
          />
        </div>

        {/* Progress section */}
        <div className="w-48 space-y-2">
          <div className="w-full bg-neutral-20 rounded-full h-2">
            <div
              className="bg-primary-50 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-neutral-50 text-center">
            {Math.round(progress)}% hoàn thành
          </p>
        </div>
        {displayAddress?.address && (
          <p className="text-lg text-secondary-70 font-bold bg-neutral-10 p-4 rounded-xl shadow-8 text-center">
            {displayAddress.address}
          </p>
        )}
      </div>
    </div>
  );
}
